import * as Blockly from 'blockly/core'
import { LuaGenerator, Order } from 'blockly/lua'
import {
  BlockDefinition,
  genericInput,
  makeDropdownField,
  numberInput,
  numberOutput,
  statementBlock,
  untypedOutput,
} from './common'
import { resizeParentCode } from './looksResize'

export const looksDefaults: Partial<BlockDefinition> = {
  style: 'looks',
}

export const looksBlocks: Array<BlockDefinition> = [
  {
    opcode: 'looks_showMessage',
    ...statementBlock,
    ...genericInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.NONE)
      return `print(${v})\n`
    },
    message0: '%{BKY_B_SHOW_MESSAGE}',
  },
  {
    opcode: 'looks_setSizeTo',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.NONE)
      return resizeParentCode(`Vector3.new(${v},${v},${v})`)
    },
    message0: '%{BKY_B_SET_SIZE_TO}',
  },
  {
    opcode: 'looks_brickColors',
    ...makeDropdownField([
      ['%{BKY_B_WHITE}', 'White'],
      ['%{BKY_B_GRAY}', 'Gray'],
      ['%{BKY_B_DARK_GRAY}', 'DarkGray'],
      ['%{BKY_B_BLACK}', 'Black'],
      ['%{BKY_B_RED}', 'Red'],
      ['%{BKY_B_YELLOW}', 'Yellow'],
      ['%{BKY_B_GREEN}', 'Green'],
      ['%{BKY_B_BLUE}', 'Blue'],
    ]),
    ...untypedOutput,
    generator: (block: Blockly.Block) => {
      const v = block.getFieldValue('VAL')
      return [`BrickColor.${v}()`, Order.ATOMIC]
    },
    message0: '%1',
  },
  {
    opcode: 'looks_setBrickColorTo',
    ...statementBlock,
    args0: [{ type: 'input_value', name: 'VAL' }],
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.BrickColor = ${v}\n`
    },
    message0: '%{BKY_B_SET_BRICK_COLOR_TO}',
  },
  {
    opcode: 'looks_materials',
    ...makeDropdownField([
      ['%{BKY_B_PLASTIC}', 'Plastic'],
      ['%{BKY_B_SMOOTH_PLASTIC}', 'SmoothPlastic'],
      ['%{BKY_B_NEON}', 'Neon'],
      ['%{BKY_B_WOOD}', 'Wood'],
      ['%{BKY_B_WOOD_PLANKS}', 'WoodPlanks'],
      ['%{BKY_B_MARBLE}', 'Marble'],
      ['%{BKY_B_BASALT}', 'Basalt'],
      ['%{BKY_B_SLATE}', 'Slate'],
      ['%{BKY_B_CRACKED_LAVA}', 'CrackedLava'],
      ['%{BKY_B_CONCRETE}', 'Concrete'],
      ['%{BKY_B_LIMESTONE}', 'Limestone'],
      ['%{BKY_B_GRANITE}', 'Granite'],
      ['%{BKY_B_PAVEMENT}', 'Pavement'],
      ['%{BKY_B_BRICK}', 'Brick'],
      ['%{BKY_B_PEBBLE}', 'Pebble'],
      ['%{BKY_B_COBBLESTONE}', 'Cobblestone'],
      ['%{BKY_B_ROCK}', 'Rock'],
      ['%{BKY_B_SANDSTONE}', 'Sandstone'],
      ['%{BKY_B_CORRODED_METAL}', 'CorrodedMetal'],
      ['%{BKY_B_DIAMOND_PLATE}', 'DiamondPlate'],
      ['%{BKY_B_FOIL}', 'Foil'],
      ['%{BKY_B_METAL}', 'Metal'],
      ['%{BKY_B_GRASS}', 'Grass'],
      ['%{BKY_B_LEAFY_GRASS}', 'LeafyGrass'],
      ['%{BKY_B_SAND}', 'Sand'],
      ['%{BKY_B_FABRIC}', 'Fabric'],
      ['%{BKY_B_SNOW}', 'Snow'],
      ['%{BKY_B_MUD}', 'Mud'],
      ['%{BKY_B_GROUND}', 'Ground'],
      ['%{BKY_B_ASPHALT}', 'Asphalt'],
      ['%{BKY_B_SALT}', 'Salt'],
      ['%{BKY_B_ICE}', 'Ice'],
      ['%{BKY_B_GLACIER}', 'Glacier'],
      ['%{BKY_B_GLASS}', 'Glass'],
      ['%{BKY_B_FORCE_FIELD}', 'ForceField'],
      ['%{BKY_B_AIR}', 'Air'],
      ['%{BKY_B_WATER}', 'Water'],
      ['%{BKY_B_CUPBOARD}', 'Cupboard'],
      ['%{BKY_B_CARPET}', 'Carpet'],
      ['%{BKY_B_CERAMIC_TILES}', 'CeramicTiles'],
      ['%{BKY_B_CLAY_ROOF_TILES}', 'ClayRoofTiles'],
      ['%{BKY_B_ROOF_SHINGLES}', 'RoofShingles'],
      ['%{BKY_B_LEATHER}', 'Leather'],
      ['%{BKY_B_PLASTER}', 'Plaster'],
      ['%{BKY_B_RUBBER}', 'Rubber'],
    ]),
    ...untypedOutput,
    generator: (block: Blockly.Block) => {
      const raw = block.getFieldValue('VAL')
      // ドロップダウン値 'Cupboard' は Enum.Material に存在しない（正しくは
      // Cardboard=段ボール）。そのまま出力すると Studio で実行時エラーになる
      // ため、生成 Lua では正しい Enum 名へ写像する。
      const v = raw === 'Cupboard' ? 'Cardboard' : raw
      return [`Enum.Material.${v}`, Order.ATOMIC]
    },
    message0: '%1',
  },
  {
    opcode: 'looks_setMaterialTo',
    ...statementBlock,
    args0: [{ type: 'input_value', name: 'VAL' }],
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.Material = ${v}\n`
    },
    message0: '%{BKY_B_SET_MATERIAL_TO}',
  },
  {
    opcode: 'looks_changeSizeBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.NONE)
      return resizeParentCode(
        `script.Parent.Size + Vector3.new(${v},${v},${v})`,
      )
    },
    message0: '%{BKY_B_CHANGE_SIZE_BY}',
  },
  {
    opcode: 'looks_show',
    ...statementBlock,
    generator: () => 'script.Parent.Transparency = 0\n',
    message0: '%{BKY_B_SHOW}',
  },
  {
    opcode: 'looks_hide',
    ...statementBlock,
    generator: () => 'script.Parent.Transparency = 1\n',
    message0: '%{BKY_B_HIDE}',
  },
  {
    opcode: 'looks_seeThrough',
    ...statementBlock,
    generator: () => 'script.Parent.Transparency = 0.5\n',
    message0: '%{BKY_B_SEE_THROUGH}',
  },
  {
    opcode: 'looks_brickColor',
    ...untypedOutput,
    generator: () => ['script.Parent.BrickColor', Order.ATOMIC],
    message0: '%{BKY_B_BRICK_COLOR}',
  },
  {
    opcode: 'looks_material',
    ...untypedOutput,
    generator: () => ['script.Parent.Material', Order.ATOMIC],
    message0: '%{BKY_B_MATERIAL}',
  },
  {
    opcode: 'looks_xSize',
    ...numberOutput,
    generator: () => ['script.Parent.Size.X', Order.ATOMIC],
    message0: '%{BKY_B_X_SIZE}',
  },
  {
    opcode: 'looks_ySize',
    ...numberOutput,
    generator: () => ['script.Parent.Size.Y', Order.ATOMIC],
    message0: '%{BKY_B_Y_SIZE}',
  },
  {
    opcode: 'looks_zSize',
    ...numberOutput,
    generator: () => ['script.Parent.Size.Z', Order.ATOMIC],
    message0: '%{BKY_B_Z_SIZE}',
  },
  {
    opcode: 'looks_setXSizeTo',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.NONE)
      return resizeParentCode(
        `Vector3.new(${v},script.Parent.Size.Y,script.Parent.Size.Z)`,
      )
    },
    message0: '%{BKY_B_SET_X_SIZE_TO}',
  },
  {
    opcode: 'looks_setYSizeTo',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.NONE)
      return resizeParentCode(
        `Vector3.new(script.Parent.Size.X,${v},script.Parent.Size.Z)`,
      )
    },
    message0: '%{BKY_B_SET_Y_SIZE_TO}',
  },
  {
    opcode: 'looks_setZSizeTo',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.NONE)
      return resizeParentCode(
        `Vector3.new(script.Parent.Size.X,script.Parent.Size.Y,${v})`,
      )
    },
    message0: '%{BKY_B_SET_Z_SIZE_TO}',
  },
  {
    opcode: 'looks_setXYZSizeTo',
    ...statementBlock,
    args0: [
      { type: 'input_value', name: 'X', check: 'Number' },
      { type: 'input_value', name: 'Y', check: 'Number' },
      { type: 'input_value', name: 'Z', check: 'Number' },
    ],
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const x = generator.valueToCode(block, 'X', Order.NONE)
      const y = generator.valueToCode(block, 'Y', Order.NONE)
      const z = generator.valueToCode(block, 'Z', Order.NONE)
      return resizeParentCode(`Vector3.new(${x},${y},${z})`)
    },
    message0: '%{BKY_B_SET_XYZ_SIZE_TO}',
  },
  {
    opcode: 'looks_changeXSizeBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return resizeParentCode(`script.Parent.Size + Vector3.new(${n},0,0)`)
    },
    message0: '%{BKY_B_CHANGE_X_SIZE_BY}',
  },
  {
    opcode: 'looks_changeYSizeBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return resizeParentCode(`script.Parent.Size + Vector3.new(0,${n},0)`)
    },
    message0: '%{BKY_B_CHANGE_Y_SIZE_BY}',
  },
  {
    opcode: 'looks_changeZSizeBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return resizeParentCode(`script.Parent.Size + Vector3.new(0,0,${n})`)
    },
    message0: '%{BKY_B_CHANGE_Z_SIZE_BY}',
  },
  {
    opcode: 'looks_setTransparencyTo',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.MULTIPLICATIVE)
      return `script.Parent.Transparency = ${v} / 100\n`
    },
    message0: '%{BKY_B_SET_TRANSPARENCY_TO}',
  },
]
